import React, { useEffect, useRef, useState } from 'react'
import { Tour } from 'antd'

import { useIdentity } from '@/hooks/auth/useIdentity'
import { useChangelogModal } from '@features/changelog'
import { useSiderContext } from '@components/layout/sider/context'
import { isProjectHubRole } from 'src/utils/projectHubRole'
import type { Account } from 'types'

import { useOnboardingTourSeen } from './useOnboardingTourSeen'
import { buildOnboardingSteps } from './tourSteps'

/**
 * First-usage guided tour, shown once to a brand-new salesman or company
 * (SALESMAN + SOCIETY — the project-hub roles). Every other role never sees it.
 *
 * "Seen once" is persisted per account in localStorage (see
 * {@link useOnboardingTourSeen}); no DB, backend, or Hasura change is involved.
 * The sider is expanded before opening so the tour's anchors are on screen.
 *
 * It waits for the changelog welcome modal, which auto-opens on the same first
 * visit. Both overlays are masked, and the tour's mask sits above the modal —
 * opening together left the modal's close button unclickable, so a brand-new
 * commercial was stuck behind two overlays neither of which could be dismissed.
 *
 * The wait is on `hasUpdates` as well as `open`: this component is a child of
 * the provider, so its effect runs first and would otherwise decide before the
 * modal it has to yield to exists. `hasUpdates` is the modal's settled "there
 * is something to show" verdict, and it only clears once the modal is
 * acknowledged.
 */
export const OnboardingTour: React.FC = () => {
  const { data: identity } = useIdentity<Account>()
  const { setCollapsed } = useSiderContext()
  const { open: changelogOpen, hasUpdates: changelogHasUpdates } =
    useChangelogModal()

  const accountId = identity?.id
  const isGuidedRole = isProjectHubRole(identity?.account_type)

  const { seen, markSeen } = useOnboardingTourSeen(accountId)

  const [open, setOpen] = useState(false)
  const autoOpened = useRef(false)
  const steps = buildOnboardingSteps()

  useEffect(() => {
    if (autoOpened.current) return
    if (!isGuidedRole || seen) return
    if (changelogOpen || changelogHasUpdates) return
    autoOpened.current = true
    setCollapsed(false)
    setOpen(true)
  }, [
    isGuidedRole,
    seen,
    changelogOpen,
    changelogHasUpdates,
    setCollapsed,
  ])

  const finish = () => {
    setOpen(false)
    markSeen()
  }

  if (!isGuidedRole) return null

  return <Tour open={open} onClose={finish} onFinish={finish} steps={steps} />
}
